import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router } from "@angular/router";
import { SiteAppCommand } from "@fabric-types/app-site";
import { Store } from "@ngrx/store";
import { CORE_ROUTE } from "@obg/ngx.core";
import { DocumentRef, PlatformService } from "@obg/ngx.global.evo";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { Observable, of } from "rxjs";
import { filter, map, switchMap, take, tap, withLatestFrom } from "rxjs/operators";

import { CommonState } from "../state";

import { GeoFencingAction } from "./geofencing.action";
import { GeofencingSelector } from "./geofencing.selector";
import { GeofencingService } from "./geofencing.service";

@Injectable({ providedIn: "root" })
export class BrazilGeofencingGuard implements CanActivate {
	private isCorrectLocation = false;
	constructor(
		private platformService: PlatformService,
		private store: Store<CommonState>,
		private geofencingService: GeofencingService,
		private docRef: DocumentRef,
		private router: Router
	) {
		// If the user's position has been identified as correct we want to set this to 'true', to bypass the geolocation tutorial flow
		// on subsequent navigations to the registration.
		if (!this.platformService.isServer && this.geofencingService.isBrazilGeofencingEnabled()) {
			this.docRef.obgEventBus?.listen(SiteAppCommand.ValidateLocationSuccess, (payload) => {
				if (!payload.isFraud) {
					this.isCorrectLocation = true;
					this.docRef.obgEventBus.dispatch(SiteAppCommand.ProceedWithIntent);
				}
			});
		}
	}

	canActivate(activateRouteSnap: ActivatedRouteSnapshot): Observable<boolean> {
		if (this.platformService.isServer || !this.geofencingService.isBrazilGeofencingEnabled()) {
			return of(true);
		}

		const registerRoute = this.router.config.find(
			(route) => (route as any).name === CORE_ROUTE.registration.register
		);
		// Checks if this is a direct URL navigation and if not stops guard early and triggers Geo permission tutorial
		const isDirectNavigation = (activateRouteSnap as any)._routerState.url === `/${registerRoute}`;

		if (!isDirectNavigation && !this.isCorrectLocation) {
			this.docRef.obgEventBus.dispatch(SiteAppCommand.StartGeoFlow, {
				intent: CORE_ROUTE.registration.register,
			});
			this.store.dispatch(
				RouteAction.navigate(
					CORE_ROUTE.home,
					{},
					{
						onSameUrlNavigation: "reload",
						queryParams: activateRouteSnap.queryParams,
					}
				)
			);
			return of(false);
		}

		return this.store.select(GeofencingSelector.isBusy).pipe(
			filter((isBusy) => !isBusy),
			switchMap(() => this.store.select(GeofencingSelector.isValid)),
			ignoreNil(),
			withLatestFrom(this.store.select(RouteSelector.getCurrent)),
			take(1),
			map(([isValid, currentRoute]) => {
				if (!isValid && !currentRoute) {
					this.docRef.obgEventBus.dispatch(SiteAppCommand.StartGeoFlow, {
						intent: CORE_ROUTE.registration.register,
					});
					this.store.dispatch(
						RouteAction.navigate(
							CORE_ROUTE.home,
							{},
							{
								onSameUrlNavigation: "reload",
								queryParams: activateRouteSnap.queryParams,
							}
						)
					);
					setTimeout(() => {
						this.docRef.obgEventBus.dispatch(SiteAppCommand.StartGeoFlow, {
							intent: CORE_ROUTE.registration.register,
						});
					}, 100);
					return false;
				}
				this.isCorrectLocation = false;
				return isValid;
			}),
			tap(() => {
				this.store.dispatch(GeoFencingAction.resetError());
			})
		);
	}
}
